/// <amd-module name="Test/TestResultDetails/Store/TestResultDetailsViewStore" />
import { IViewContextData, TestOutcomeEnum } from "Test/common-util/Common";
import { TestCaseResult, TestRun, TestSubResult, TestTag } from "Test/common-util/Contracts/Test";
import { ITestDetailsPanePayload, ITestResultDetails } from "Test/common-util/Contracts/ViewContracts";
import * as Telemetry from "Test/common-util/Telemetry";
import * as TelemetryConstants from 'Test/TestResultDetails/TelemetryConstants';
import { TestResultDetailsActionHub } from "Test/TestResultDetails/Actions/TestResultDetailsActionHub";
import { Store } from "VSS/Reflux/Store";
import { IVssPageContext } from 'VSS/Platform/Context';
import { TestResultDetailsPanePivot, TestMode } from "Test/TestResultDetails/Actions/TestResultDetailsActionHub";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

export interface ITestRunDetails {
    name: string;
    duration: string;
    runId: number;
    errorMessage?: string;
    comment?: string;
    state?: string;
    startedDate: Date;
    completedDate: Date;
    tags?: string[];
}

export interface ITestResultDetailsViewState {
    isLoading: boolean;
    loadingErrorMessage: string;
    testResults?: ITestResultDetails;
    testRun?: ITestRunDetails;
    testMode: TestMode;
    selectedPivot: string;
    currentHeaderState: boolean;
}

export class TestResultDetailsViewStore extends Store {

    constructor(changeEvent: string,
        private _actionsHub: TestResultDetailsActionHub,
        private _context: IVssPageContext,
        private _viewContextData: IViewContextData) {
        super(changeEvent);
        this._initialize();
        this._actionsHub.updateDetailsPane.addListener(this._updateDetailPane);
        this._actionsHub.cleanDetailsPane.addListener(this._cleanDetailPane);
        this._actionsHub.changeDetailsPivot.addListener(this._changeDetailPivot);
        this._actionsHub.onError.addListener(this._onErrorListener);
        this._actionsHub.captureHeaderState.addListener(this._captureHeaderState);
    }

    public getState(): ITestResultDetailsViewState {
        return this._state;
    }

    public dispose() {
        this._actionsHub.updateDetailsPane.removeListener(this._updateDetailPane);
        this._actionsHub.cleanDetailsPane.removeListener(this._cleanDetailPane);
        this._actionsHub.changeDetailsPivot.removeListener(this._changeDetailPivot);
        this._actionsHub.onError.removeListener(this._onErrorListener);
        this._actionsHub.captureHeaderState.removeListener(this._captureHeaderState);
    }

    private _initialize(): void {
        this._state = this._getTestResultDetailsDefaultState();
    }

    private _getTestResultDetailsDefaultState(): ITestResultDetailsViewState {
        return {
            isLoading: true,
            testMode: TestMode.TestResult,
            selectedPivot: TestResultDetailsPanePivot.Debug,
            currentHeaderState: false
        } as ITestResultDetailsViewState;
    }

    private _cleanDetailPane = () => {
        this._state = this._getTestResultDetailsDefaultState();
        this.emitChanged();
    }

    private _updateDetailPane = (data: ITestDetailsPanePayload) => {
        if (!!data) {
            if (data.storeTestCaseResultTreeData && !data.storeTestCaseResultTreeData.isTestCaseRow) {
                this._editStateForTestRun(data);
            } else {
                this._editStateForTestResult(data);
            }
            this._state.isLoading = false;
            this._state.loadingErrorMessage = "";

            this._state.selectedPivot = data.openPane != undefined &&  data.openPane != "" ? data.openPane : TestResultDetailsPanePivot.Debug;
            this.emitChanged();
        }
    }

    private _editStateForTestResult(data: ITestDetailsPanePayload): void {
        if (data && data.detailedTestResult) {
            const treeNodeData = data.storeTestCaseResultTreeData;
            if (treeNodeData) {
                const detailedData: TestCaseResult | TestSubResult | undefined = treeNodeData.subResultId ?
                    this._getSubResultById(data.detailedTestResult.subResults, treeNodeData.subResultId, 1)
                    : data.detailedTestResult;

                this._state.testMode = TestMode.TestResult;
                if (detailedData) {
                    this._state.testResults = {
                        test: treeNodeData.test,
                        outcome: treeNodeData.outcome ? treeNodeData.outcome : (<any>TestOutcomeEnum)[data.detailedTestResult.outcome],
                        duration: treeNodeData.duration,
                        failingContextName: treeNodeData.failingContextName,
                        failingContextId: treeNodeData.failingContextId,
                        isCurrentArtifact: treeNodeData.isCurrentArtifact,
                        owner: treeNodeData.owner ? treeNodeData.owner : data.detailedTestResult.owner ? data.detailedTestResult.owner.displayName : undefined,
                        runId: treeNodeData.runId,
                        subResultId: (treeNodeData.subResultId) ? treeNodeData.subResultId : 0,
                        state: data.detailedTestResult.state,
                        startedDate: detailedData.startedDate,
                        completedDate: detailedData.completedDate,
                        errorMessage: detailedData.errorMessage ? detailedData.errorMessage : "",
                        comment: detailedData.comment ? detailedData.comment : "",
                        stackTrace: detailedData.stackTrace ? detailedData.stackTrace : "",
                        computerName: detailedData.computerName ? detailedData.computerName : "",
                        result: data.detailedTestResult,
                        failingSince: treeNodeData.failingSince,
                    } as ITestResultDetails;

                    // TODO: TagsWork
                    // if (isFeatureFlagEnabled(this._context.pageContext, this._tagsForTestRunFF, false)) {
                    //     this._state.testResults.tags: detailedData.tags
                    // }
                }
            }
        }
    }

    private _editStateForTestRun(data: ITestDetailsPanePayload): void {

        if (data && data.testRun) {
            const testRun: TestRun = data.testRun;
            const treeNodeData = data.storeTestCaseResultTreeData;

            if (treeNodeData) {
                this._state.testMode = TestMode.TestRun;
                this._state.testRun = {
                    name: testRun.name,
                    duration: treeNodeData.duration,
                    runId: testRun.id,
                    errorMessage: testRun.errorMessage ? testRun.errorMessage : "",
                    comment: testRun.comment ? testRun.comment : "",
                    state: testRun.state,
                    startedDate: testRun.startedDate,
                    completedDate: testRun.completedDate,
                } as ITestRunDetails;

                if (testRun.tags) {
                    this._state.testRun.tags = testRun.tags.map(tag => tag.name);
                }
            }

            if (testRun.comment) {
                // Send telemetry that test run comment exists
                Telemetry.publishEvent(this._context, this._viewContextData, TelemetryConstants.featureTestTab_TestRunComment, { "count": 1 });
            }
        }

    }

    private _getSubResultById(subResults: TestSubResult[], id: number, depth: number): TestSubResult | undefined {
        let subResult: TestSubResult | undefined;
        if (depth > 3) {
            return;
        }
        let index: number = -1;
        for (let i = 0; i < subResults.length; ++i) {
            if (subResults[i].id === id) {
                index = i;
            }
        }
        if (index >= 0) {
            subResult = subResults[index];
        } else {
            for (let i = 0; i < subResults.length; ++i) {
                if (subResults[i].subResults) {
                    subResult = this._getSubResultById(subResults[i].subResults, id, depth + 1);
                    if (subResult) {
                        break;
                    }
                }
            }
        }

        return subResult;
    }

    private _onErrorListener = (errorMessage: string): void => {
        this._state.loadingErrorMessage = errorMessage;
        this._state.isLoading = false;

        this.emitChanged();
    }

    private _changeDetailPivot = (pivotKey: string): void => {
        this._state.selectedPivot = pivotKey;

        this.emitChanged();
    }

    private _captureHeaderState = (currentState: boolean): void => {
        this._state.currentHeaderState = currentState;
    }

    private _state: ITestResultDetailsViewState;
    private _tagsForTestRunFF: string = "TestManagement.Server.TagsInTestRun";
}
